import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { timeEntriesAPI, timeLookupsAPI, timeProfilesAPI, timeReportsAPI } from '../services/api';
import { TimeCustomerLookup, TimeEntry, TimeProfile, TimeProjectLookup, TimeProjectTaskLookup } from '../types';
import { endOfWeekIso, formatDate, formatDurationMinutes, minutesToParts, partsToMinutes, startOfWeekIso, todayIso } from '../utils';
import { resolveEntryProfileId } from '../utils/entryProfile';
import DateInput from './DateInput';
import TimeSelect from './TimeSelect';

interface CalendarEntryFormState {
  timeProfileId: number;
  customerId: number;
  customerQuery: string;
  projectId: number;
  projectQuery: string;
  projectTaskId: number;
  projectTaskQuery: string;
  workDate: string;
  hours: number | '';
  minutes: number | '';
  isBillable: boolean;
  description: string;
}

const defaultEntryForm: CalendarEntryFormState = {
  timeProfileId: 0,
  customerId: 0,
  customerQuery: '',
  projectId: 0,
  projectQuery: '',
  projectTaskId: 0,
  projectTaskQuery: '',
  workDate: todayIso(),
  hours: '',
  minutes: '',
  isBillable: true,
  description: '',
};

const isoToDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dayLabel = (date: Date) => date.toLocaleDateString(undefined, { weekday: 'long' });

const CalendarPage: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [projectTasks, setProjectTasks] = useState<TimeProjectTaskLookup[]>([]);
  const [isEntryFormOpen, setEntryFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<TimeEntry | null>(null);
  const [entryForm, setEntryForm] = useState<CalendarEntryFormState>(defaultEntryForm);
  const [loading, setLoading] = useState(true);
  const [entrySaving, setEntrySaving] = useState(false);

  const range = useMemo(() => {
    const date = isoToDate(selectedDate);
    return {
      startDate: startOfWeekIso(date),
      endDate: endOfWeekIso(date),
    };
  }, [selectedDate]);

  const days = useMemo(() => {
    const start = isoToDate(range.startDate);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return toIsoDate(date);
    });
  }, [range.startDate]);

  const formatProjectOption = (project: TimeProjectLookup) => `${project.customerTitle} - ${project.title}`;
  const entryProjects = useMemo(() => {
    if (!entryForm.customerId) {
      return projects;
    }

    return projects.filter(project => project.customerId === entryForm.customerId);
  }, [entryForm.customerId, projects]);

  const entryProjectTasks = useMemo(() => {
    if (!entryForm.projectId) {
      return [];
    }

    return projectTasks.filter(task => task.projectId === entryForm.projectId);
  }, [entryForm.projectId, projectTasks]);

  const formatProjectTaskOption = (task: TimeProjectTaskLookup) => task.projectId === entryForm.projectId
    ? task.title
    : `${task.customerTitle} - ${task.projectTitle} - ${task.title}`;

  const applyCustomerQuery = (value: string) => {
    const matchedCustomer = customers.find(customer => customer.title.toLowerCase() === value.trim().toLowerCase());
    setEntryForm(prev => {
      const customerId = matchedCustomer?.id || 0;
      const currentProjectStillMatches = customerId && projects.some(project => project.id === prev.projectId && project.customerId === customerId);

      return {
        ...prev,
        customerId,
        customerQuery: value,
        projectId: currentProjectStillMatches ? prev.projectId : 0,
        projectQuery: currentProjectStillMatches ? prev.projectQuery : '',
        projectTaskId: currentProjectStillMatches ? prev.projectTaskId : 0,
        projectTaskQuery: currentProjectStillMatches ? prev.projectTaskQuery : '',
      };
    });
  };

  const applyProjectQuery = (value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const matchedProject = entryProjects.find(project =>
      project.title.toLowerCase() === normalizedValue ||
      formatProjectOption(project).toLowerCase() === normalizedValue
    );

    setEntryForm(prev => ({
      ...prev,
      projectId: matchedProject?.id || 0,
      projectQuery: value,
      projectTaskId: 0,
      projectTaskQuery: '',
      customerId: matchedProject?.customerId || prev.customerId,
      customerQuery: matchedProject?.customerTitle || prev.customerQuery,
    }));
  };

  const applyProjectTaskQuery = (value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const matchedTask = entryProjectTasks.find(task =>
      task.title.toLowerCase() === normalizedValue ||
      formatProjectTaskOption(task).toLowerCase() === normalizedValue
    );

    setEntryForm(prev => ({
      ...prev,
      projectTaskId: matchedTask?.id || 0,
      projectTaskQuery: value,
    }));
  };

  const loadEntries = () => {
    setLoading(true);
    return timeReportsAPI.getDetailed({ startDate: range.startDate, endDate: range.endDate, skip: 0, take: 500 })
      .then(data => setEntries(data.items))
      .catch(() => toast.error('Calendar entries could not be loaded'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([
      timeProfilesAPI.getProfiles(false),
      timeLookupsAPI.getCustomers(),
      timeLookupsAPI.getProjects(),
      timeLookupsAPI.getProjectTasks(),
    ])
      .then(([profileData, customerData, projectData, projectTaskData]) => {
        setProfiles(profileData);
        setCustomers(customerData);
        setProjects(projectData);
        setProjectTasks(projectTaskData);
        setEntryForm(prev => ({
          ...prev,
          timeProfileId: resolveEntryProfileId(profileData, user?.email, isAdmin, prev.timeProfileId),
        }));
      })
      .catch(() => toast.error('Lookups could not be loaded'));
  }, [isAdmin, user?.email]);

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.startDate, range.endDate]);

  const totalMinutes = useMemo(
    () => entries.reduce((sum, entry) => sum + entry.durationMinutes, 0),
    [entries]
  );

  const entriesByDate = useMemo(() => {
    const groups = new Map<string, TimeEntry[]>();
    entries.forEach(entry => {
      const dateKey = entry.workDate.slice(0, 10);
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(entry);
    });
    return groups;
  }, [entries]);

  const openEntryEditor = (entry: TimeEntry) => {
    const duration = minutesToParts(entry.durationMinutes);
    const project = projects.find(item => item.id === entry.projectId);
    const customerId = entry.customerId || project?.customerId || 0;
    const customerTitle = entry.customerTitle || project?.customerTitle || '';
    setEditingEntry(entry);
    setEntryForm({
      timeProfileId: entry.timeProfileId,
      customerId,
      customerQuery: customerTitle,
      projectId: entry.projectId || 0,
      projectQuery: project ? formatProjectOption(project) : entry.customerTitle && entry.projectTitle ? `${entry.customerTitle} - ${entry.projectTitle}` : entry.projectTitle || '',
      projectTaskId: entry.projectTaskId || 0,
      projectTaskQuery: entry.projectTaskTitle || '',
      workDate: entry.workDate.split('T')[0],
      hours: duration.hours,
      minutes: duration.minutes,
      isBillable: entry.isBillable,
      description: entry.description || '',
    });
    setEntryFormOpen(true);
  };

  const openNewEntry = (workDate = selectedDate) => {
    setEditingEntry(null);
    setEntryForm(prev => ({
      ...defaultEntryForm,
      timeProfileId: resolveEntryProfileId(profiles, user?.email, isAdmin, prev.timeProfileId),
      workDate,
    }));
    setEntryFormOpen(true);
  };

  const copyEntry = (entry: TimeEntry) => {
    const duration = minutesToParts(entry.durationMinutes);
    const project = projects.find(item => item.id === entry.projectId);
    const customerId = entry.customerId || project?.customerId || 0;
    const customerTitle = entry.customerTitle || project?.customerTitle || '';
    setEditingEntry(null);
    setEntryForm({
      timeProfileId: entry.timeProfileId,
      customerId,
      customerQuery: customerTitle,
      projectId: entry.projectId || 0,
      projectQuery: project ? formatProjectOption(project) : entry.customerTitle && entry.projectTitle ? `${entry.customerTitle} - ${entry.projectTitle}` : entry.projectTitle || '',
      projectTaskId: entry.projectTaskId || 0,
      projectTaskQuery: entry.projectTaskTitle || '',
      workDate: entry.workDate.split('T')[0],
      hours: duration.hours,
      minutes: duration.minutes,
      isBillable: entry.isBillable,
      description: entry.description || '',
    });
    setEntryFormOpen(true);
  };

  const closeEntryEditor = () => {
    setEditingEntry(null);
    setEntryFormOpen(false);
    setEntryForm(prev => ({
      ...defaultEntryForm,
      timeProfileId: resolveEntryProfileId(profiles, user?.email, isAdmin, prev.timeProfileId),
    }));
  };

  const handleEntrySubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (entrySaving) {
      return;
    }

    if (!entryForm.timeProfileId || !entryForm.projectId) {
      toast.error('Entry cannot be saved without a member and project');
      return;
    }

    const durationMinutes = partsToMinutes(entryForm.hours, entryForm.minutes);
    if (durationMinutes <= 0) {
      toast.error('Entry duration must be greater than 0');
      return;
    }

    try {
      const payload = {
        timeProfileId: entryForm.timeProfileId,
        projectId: entryForm.projectId,
        projectTaskId: entryForm.projectTaskId || undefined,
        workDate: entryForm.workDate,
        durationMinutes,
        isBillable: entryForm.isBillable,
        description: entryForm.description || undefined,
      };

      setEntrySaving(true);
      if (editingEntry) {
        await timeEntriesAPI.updateEntry(editingEntry.id, {
          ...payload,
          id: editingEntry.id,
        });
        toast.success('Entry updated');
      } else {
        await timeEntriesAPI.createEntry(payload);
        toast.success('Entry created');
      }
      closeEntryEditor();
      await loadEntries();
    } catch {
      toast.error('Entry could not be saved');
    } finally {
      setEntrySaving(false);
    }
  };

  const deleteEntry = async () => {
    if (!entryToDelete) {
      return;
    }

    try {
      await timeEntriesAPI.deleteEntry(entryToDelete.id);
      toast.success('Entry deleted');
      setEntryToDelete(null);
      await loadEntries();
    } catch {
      toast.error('Entry could not be deleted');
    }
  };

  const getDeleteEntryTitle = (entry: TimeEntry) => {
    const projectTitle = entry.projectTitle || 'Internal';
    return `${formatDate(entry.workDate)} • ${projectTitle} • ${formatDurationMinutes(entry.durationMinutes)}`;
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Calendar</h1>
          <p>{formatDate(range.startDate)} - {formatDate(range.endDate)}</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => openNewEntry(selectedDate)}>
          <i className="fa-solid fa-plus" aria-hidden="true" />
          New entry
        </button>
      </div>

      <div className="stat-grid calendar-summary-grid">
        <div className="stat-card">
          <span>Total hours</span>
          <strong>{formatDurationMinutes(totalMinutes)}</strong>
        </div>
      </div>

      <section className="card">
        <div className="card-body">
          <div className="calendar-toolbar">
            <label className="form-field calendar-date-field" htmlFor="calendar-date">
              <span>Calendar date</span>
              <DateInput
                id="calendar-date"
                className="form-control"
                value={selectedDate}
                onChange={(value) => setSelectedDate(value)}
              />
            </label>
            <button className="btn btn-outline-secondary" type="button" onClick={() => setSelectedDate(todayIso())}>
              Today
            </button>
          </div>
        </div>
      </section>

      {isEntryFormOpen && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeEntryEditor();
            }
          }}
        >
          <section className="time-modal entry-modal entry-modal-wide" role="dialog" aria-modal="true" aria-labelledby="calendar-entry-dialog-title">
            <div className="time-modal-header">
              <h5 id="calendar-entry-dialog-title" className="card-title">{editingEntry ? 'Edit entry' : 'New entry'}</h5>
              <button className="icon-button" type="button" onClick={closeEntryEditor} aria-label="Close entry editor">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <form className="time-form entry-form" onSubmit={handleEntrySubmit}>
                {isAdmin && (
                  <div className="form-group">
                    <label className="form-label" htmlFor="calendar-entry-profile">Member</label>
                    <TimeSelect
                      inputId="calendar-entry-profile"
                      ariaLabel="Member"
                      options={[
                        { value: 0, label: 'Member' },
                        ...profiles.map(profile => ({ value: profile.id, label: profile.displayName })),
                      ]}
                      value={entryForm.timeProfileId || 0}
                      onChange={(value) => setEntryForm(prev => ({ ...prev, timeProfileId: Number(value || 0) }))}
                      placeholder="Member"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label" htmlFor="calendar-entry-date">Date</label>
                  <DateInput
                    id="calendar-entry-date"
                    className="form-control"
                    value={entryForm.workDate}
                    onChange={(value) => setEntryForm(prev => ({ ...prev, workDate: value }))}
                    required
                  />
                </div>
                <div className="duration-grid entry-duration-grid">
                  <div className="form-group">
                    <label className="form-label" htmlFor="calendar-entry-hours">Hours</label>
                    <input
                      id="calendar-entry-hours"
                      className="form-control"
                      type="number"
                      min="0"
                      max="24"
                      value={entryForm.hours}
                      onChange={(event) => setEntryForm(prev => ({ ...prev, hours: event.target.value === '' ? '' : Number(event.target.value) }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="calendar-entry-minutes">Minutes</label>
                    <input
                      id="calendar-entry-minutes"
                      className="form-control"
                      type="number"
                      min="0"
                      max="59"
                      value={entryForm.minutes}
                      onChange={(event) => setEntryForm(prev => ({ ...prev, minutes: event.target.value === '' ? '' : Number(event.target.value) }))}
                    />
                  </div>
                </div>
                <div className="entry-lookup-grid entry-lookup-grid-wide">
                  <div className="form-group entry-client-field">
                    <label className="form-label" htmlFor="calendar-entry-client">Client</label>
                    <TimeSelect
                      inputId="calendar-entry-client"
                      ariaLabel="Client"
                      options={customers.map(customer => ({ value: customer.id, label: customer.title }))}
                      value={entryForm.customerId || null}
                      onChange={(_, option) => applyCustomerQuery(option?.label || '')}
                      placeholder="Start typing a client"
                      noOptionsMessage="No clients"
                    />
                  </div>
                  <div className="form-group entry-project-field">
                    <label className="form-label" htmlFor="calendar-entry-project">Project</label>
                    <TimeSelect
                      inputId="calendar-entry-project"
                      ariaLabel="Project"
                      options={entryProjects.map(project => ({ value: project.id, label: formatProjectOption(project) }))}
                      value={entryForm.projectId || null}
                      onChange={(_, option) => applyProjectQuery(option?.label || '')}
                      placeholder={entryForm.customerId ? 'Start typing a project' : 'Select a client first'}
                      noOptionsMessage="No projects"
                    />
                  </div>
                  <div className="form-group entry-task-field">
                    <label className="form-label" htmlFor="calendar-entry-task">Task <span className="optional-label">optional</span></label>
                    <TimeSelect
                      inputId="calendar-entry-task"
                      ariaLabel="Task"
                      options={entryProjectTasks.map(task => ({ value: task.id, label: formatProjectTaskOption(task) }))}
                      value={entryForm.projectTaskId || null}
                      onChange={(_, option) => applyProjectTaskQuery(option?.label || '')}
                      placeholder={entryForm.projectId ? 'Start typing a task' : 'Select a project first'}
                      isClearable
                      isDisabled={!entryForm.projectId || entryProjectTasks.length === 0}
                      noOptionsMessage="No tasks"
                    />
                  </div>
                </div>
                <label className="toggle-row entry-toggle-row">
                  <input
                    type="checkbox"
                    checked={entryForm.isBillable}
                    onChange={(event) => setEntryForm(prev => ({ ...prev, isBillable: event.target.checked }))}
                  />
                  Billable
                </label>
                <div className="form-group entry-description-field">
                  <label className="form-label" htmlFor="calendar-entry-description">Description</label>
                  <textarea
                    id="calendar-entry-description"
                    className="form-control entry-description-control"
                    rows={5}
                    value={entryForm.description}
                    onChange={(event) => setEntryForm(prev => ({ ...prev, description: event.target.value }))}
                  />
                </div>
                <div className="form-actions">
                  <button className="btn btn-primary" type="submit" disabled={entrySaving} aria-busy={entrySaving}>
                    {entrySaving ? (
                      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    ) : (
                      <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
                    )}
                    {entrySaving ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={closeEntryEditor}>Cancel</button>
                </div>
              </form>
            </div>
          </section>
        </div>
      )}

      {entryToDelete && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setEntryToDelete(null);
            }
          }}
        >
          <section className="time-modal confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="calendar-delete-entry-dialog-title">
            <div className="time-modal-header">
              <h5 id="calendar-delete-entry-dialog-title" className="card-title">Delete entry?</h5>
              <button className="icon-button" type="button" onClick={() => setEntryToDelete(null)} aria-label="Close delete confirmation">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <p className="confirmation-text">Are you sure you want to delete this time entry?</p>
              <p className="confirmation-detail">{getDeleteEntryTitle(entryToDelete)}</p>
              <div className="form-actions confirmation-actions">
                <button className="btn btn-secondary" type="button" onClick={() => setEntryToDelete(null)}>
                  Cancel
                </button>
                <button className="btn btn-danger" type="button" onClick={deleteEntry}>
                  <i className="fa-solid fa-trash" aria-hidden="true" />
                  Delete entry
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      <section className="calendar-grid" aria-label="Week calendar visualization">
        {days.map(day => {
          const dayEntries = entriesByDate.get(day) || [];
          const totalMinutes = dayEntries.reduce((sum, entry) => sum + entry.durationMinutes, 0);
          const date = isoToDate(day);
          const isCurrentDay = day === todayIso();
          return (
            <article className="calendar-day-card" role="region" aria-label={`${dayLabel(date)} ${formatDate(day)}`} key={day}>
              <div className={`calendar-day-header ${isCurrentDay ? 'current-day-title' : ''}`}>
                <div>
                  <strong>{dayLabel(date)}</strong>
                  <span>{formatDate(day)}</span>
                </div>
                <div className="calendar-day-actions">
                  <strong>{formatDurationMinutes(totalMinutes)}</strong>
                  <button className="action-btn" type="button" onClick={() => openNewEntry(day)} aria-label={`Create entry for ${formatDate(day)}`}>
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="calendar-entry-list">
                {dayEntries.map(entry => (
                  <div className={`calendar-entry ${entry.isBillable ? 'billable' : 'non-billable'}`} key={entry.id}>
                    <div className="calendar-entry-title">{entry.description || 'Time entry'}</div>
                    <div className="calendar-entry-meta">{entry.customerTitle || 'Unassigned'} · {entry.projectTitle || 'Internal'}</div>
                    <div className="calendar-entry-hours">{formatDurationMinutes(entry.durationMinutes)}</div>
                    <button className="action-btn" type="button" onClick={() => openEntryEditor(entry)} aria-label={`Edit ${entry.description || entry.projectTitle || 'entry'}`}>
                      <i className="fa-solid fa-pen" aria-hidden="true" />
                    </button>
                    <button className="action-btn" type="button" onClick={() => copyEntry(entry)} aria-label={`Copy ${entry.description || entry.projectTitle || 'entry'}`}>
                      <i className="fa-solid fa-copy" aria-hidden="true" />
                    </button>
                    <button className="action-btn danger" type="button" onClick={() => setEntryToDelete(entry)} aria-label={`Delete ${entry.description || entry.projectTitle || 'entry'}`}>
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                ))}
                {!loading && dayEntries.length === 0 && <div className="empty-state compact">No entries</div>}
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
};

export default CalendarPage;
